/* eslint-disable react-refresh/only-export-components -- canvas adapters exported alongside registry */
import type { ChartKind, ChartRenderer, ChartCanvasProps, MultiSeriesAreaData, CompositionDonutData, SparklineChartData } from '../charts'
import { Chart } from '../../Common/Chart/Chart'
import type { ChartResult } from '../../../types/chart'
import { useNls } from '../../../nls/nlsProvider'
import { MAIN_CHART_SERIES, COMPOSITION_COLORS } from '../tabs/analysisTabShared'
import { createAnalysisChartSpec } from './analysisChartMapping'
import { BlockerGraphChart } from '../charts/BlockerGraphChart'
import { HorizontalBarChart } from '../charts/HorizontalBarChart'
import { ReadinessMatrixChart } from '../charts/ReadinessMatrixChart'
import { ScatterChart } from '../charts/ScatterChart'
import { ScoreRingChart } from '../charts/ScoreRingChart'
import { SubScoreBarsChart } from '../charts/SubScoreBarsChart'

const CommonLineAreaCanvas = ({ data, width, height, descriptor }: ChartCanvasProps<MultiSeriesAreaData>) => {
  const { t } = useNls()
  const title = descriptor?.title || t('NLSAnalysisSignalsOverTime')
  const seriesColor = Object.fromEntries(
    (data?.series ?? []).map((s, idx) => {
      const key = s.id || `s-${idx}`
      const accent =
        MAIN_CHART_SERIES.find((m) => m.id === s.id)?.accent ||
        s.color ||
        'var(--chart-1)'
      return [key, accent]
    })
  )
  const spec = createAnalysisChartSpec({
    id: 'analysis-area-chart',
    title,
    type: 'area',
    style: {
      seriesColor,
    },
  })
  const chartResult: ChartResult = {
    series: (data?.series ?? []).map((s, idx) => ({
      key: s.id || `s-${idx}`,
      label: s.label,
      points: (s.points ?? []).map((p) => ({ x: p.label, y: p.value })),
    })),
    asOf: new Date().toISOString(),
  }
  return <Chart spec={spec} data={chartResult} width={width} height={height} />
}

const CommonDonutCanvas = ({ data, width, height, descriptor }: ChartCanvasProps<CompositionDonutData>) => {
  const { t } = useNls()
  const title = descriptor?.title || t('NLSAnalysisComposition')
  const segments = data?.segments ?? []
  const seriesColor = Object.fromEntries(
    segments.map((seg) => {
      const color =
        COMPOSITION_COLORS[seg.id as keyof typeof COMPOSITION_COLORS] ||
        seg.color ||
        'var(--chart-1)'
      return [seg.id || seg.label, color]
    })
  )
  const spec = createAnalysisChartSpec({
    id: 'analysis-donut-chart',
    title,
    type: 'donut',
    style: {
      seriesColor,
    },
  })
  const chartResult: ChartResult = {
    series: [
      {
        key: 'composition',
        label: title,
        points: segments.map((seg) => ({ x: seg.label, y: seg.value })),
      },
    ],
    total: segments.reduce((acc, seg) => acc + seg.value, 0),
    asOf: new Date().toISOString(),
  }
  return <Chart spec={spec} data={chartResult} width={width} height={height} />
}

const CommonSparklineCanvas = ({ data, width, height, descriptor }: ChartCanvasProps<SparklineChartData>) => {
  const { t } = useNls()
  const title = descriptor?.title || data?.label || t('NLSAnalysisTrend')
  const spec = createAnalysisChartSpec({
    id: 'analysis-sparkline-chart',
    title,
    type: 'line',
    style: {
      legend: 'off',
      seriesColor: {
        sparkline: 'var(--chart-6)',
      },
    },
  })
  const rawPoints = data?.points ?? []
  const points = rawPoints.length ? rawPoints : [0]
  const chartResult: ChartResult = {
    series: [
      {
        key: 'sparkline',
        label: title,
        points: points.map((val, idx) => ({ x: String(idx + 1), y: val })),
      },
    ],
    asOf: new Date().toISOString(),
  }
  return <Chart spec={spec} data={chartResult} width={width} height={height} />
}

export const CHART_REGISTRY: Record<ChartKind, ChartRenderer> = {
  'multi-series-area': { kind: 'multi-series-area', Canvas: CommonLineAreaCanvas as ChartRenderer['Canvas'], defaultRecordColumns: ['primary', 'numeric'] },
  'composition-donut': { kind: 'composition-donut', Canvas: CommonDonutCanvas as ChartRenderer['Canvas'], defaultRecordColumns: ['primary', 'numeric'] },
  sparkline: { kind: 'sparkline', Canvas: CommonSparklineCanvas as ChartRenderer['Canvas'], defaultRecordColumns: ['primary', 'numeric'] },
  'score-ring': { kind: 'score-ring', Canvas: ScoreRingChart as ChartRenderer['Canvas'], defaultRecordColumns: ['primary', 'secondary', 'numeric'] },
  'sub-score-bars': { kind: 'sub-score-bars', Canvas: SubScoreBarsChart as ChartRenderer['Canvas'], defaultRecordColumns: ['primary', 'numeric'] },
  'metric-history': { kind: 'metric-history', Canvas: CommonSparklineCanvas as ChartRenderer['Canvas'], defaultRecordColumns: ['primary', 'numeric'] },
  'blocker-graph': { kind: 'blocker-graph', Canvas: BlockerGraphChart as ChartRenderer['Canvas'], defaultRecordColumns: ['primary', 'secondary', 'numeric'] },
  'drift-scatter': { kind: 'drift-scatter', Canvas: ScatterChart as ChartRenderer['Canvas'], defaultRecordColumns: ['primary', 'secondary', 'numeric'] },
  'readiness-matrix': { kind: 'readiness-matrix', Canvas: ReadinessMatrixChart as ChartRenderer['Canvas'], defaultRecordColumns: ['primary', 'secondary'] },
  'assignee-bars': { kind: 'assignee-bars', Canvas: HorizontalBarChart as ChartRenderer['Canvas'], defaultRecordColumns: ['primary', 'numeric'] },
}
